import { useEffect, useState } from "react";
import { BarChart3, MousePointerClick, Clock } from "lucide-react";
import { track } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  view_landing: "Startseite geöffnet",
  view_live: "LiveAnzeige Wiesbaden",
  view_simulation: "Simulationsanzeige",
  view_dual: "Tablet/Desktopmodus",
  view_dashboard: "Dashboard geöffnet",
  view_statistik: "Statistik geöffnet",
  scan_open: "Kennzeichenscan gestartet",
  plate_recognized: "Kennzeichen erkannt",
  einfahrt: "Einfahrt gebucht",
  ausfahrt: "Ausfahrt gebucht",
};
const label = (e) => LABELS[e] || e;

function fmtTs(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (e) { return iso; }
}

export default function Statistik() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/stats`);
      const j = await r.json();
      setStats(j);
      setErr(null);
    } catch (e) { setErr("Statistik konnte nicht geladen werden"); }
  };

  useEffect(() => { track("view_statistik"); load(); }, []);

  const events = stats ? Object.entries(stats.by_event).sort((a, b) => b[1] - a[1]) : [];
  const maxEvent = events.length ? events[0][1] : 1;
  const maxDay = stats && stats.by_day.length ? Math.max(...stats.by_day.map((d) => d.count)) : 1;

  return (
    <>
      <div className="al-content-header">
        <h1>Aufrufstatistik</h1>
        <div className="al-breadcrumb"><span>Start</span><span>/</span><span style={{ color: "#212529" }}>Statistik</span></div>
      </div>

      <div className="al-content" data-testid="statistik-page">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
          <div className="small-box bg-al-primary" data-testid="stat-total">
            <div className="inner"><h3>{stats ? stats.total : "…"}</h3><p>Aufrufe & Aktionen gesamt</p></div>
            <div className="al-icon"><BarChart3 size={70} strokeWidth={1.5} /></div>
            <button className="small-box-footer" onClick={load} style={{ border: "none", cursor: "pointer" }}>Aktualisieren</button>
          </div>
          <div className="small-box bg-al-info" data-testid="stat-views">
            <div className="inner"><h3>{stats ? (stats.by_event.view_landing || 0) : "…"}</h3><p>Startseiten-Aufrufe</p></div>
            <div className="al-icon"><MousePointerClick size={70} strokeWidth={1.5} /></div>
            <span className="small-box-footer">Landing</span>
          </div>
          <div className="small-box bg-al-success" data-testid="stat-scans">
            <div className="inner"><h3>{stats ? (stats.by_event.plate_recognized || 0) : "…"}</h3><p>Kennzeichen erkannt</p></div>
            <div className="al-icon"><MousePointerClick size={70} strokeWidth={1.5} /></div>
            <span className="small-box-footer">OCR</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="al-card">
            <div className="al-card-header"><BarChart3 size={18} className="title-icon" /> Was wurde gemacht?</div>
            <div className="al-card-body">
              {err && <div style={{ color: "#dc3545" }}>{err}</div>}
              {events.length === 0 && !err && <div style={{ color: "#6c757d" }}>Noch keine Daten erfasst.</div>}
              {events.map(([ev, count]) => (
                <div key={ev} style={{ marginBottom: 12 }} data-testid={`stat-row-${ev}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{label(ev)}</span><span style={{ fontWeight: 700 }}>{count}</span>
                  </div>
                  <div style={{ height: 8, background: "#eef1f4", borderRadius: 6, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${(count / maxEvent) * 100}%`, background: "#0d6efd", borderRadius: 6 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="al-card">
            <div className="al-card-header"><Clock size={18} className="title-icon" /> Aufrufe pro Tag</div>
            <div className="al-card-body">
              {stats && stats.by_day.length > 0 ? (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 180, paddingTop: 10 }}>
                  {stats.by_day.map((d) => (
                    <div key={d.day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: "0.72rem", color: "#495057", fontWeight: 700 }}>{d.count}</div>
                      <div title={`${d.day}: ${d.count}`} style={{ width: "100%", maxWidth: 34, height: `${(d.count / maxDay) * 130}px`, background: "linear-gradient(180deg,#0dcaf0,#0d6efd)", borderRadius: "6px 6px 0 0", minHeight: 4 }} />
                      <div style={{ fontSize: "0.68rem", color: "#6c757d" }}>{d.day.slice(5)}</div>
                    </div>
                  ))}
                </div>
              ) : <div style={{ color: "#6c757d" }}>Noch keine Daten.</div>}
            </div>
          </div>
        </div>

        <div className="al-card">
          <div className="al-card-header"><Clock size={18} className="title-icon" /> Letzte Aktivitäten</div>
          <div className="al-card-body p-0">
            <div style={{ overflowX: "auto" }}>
              <table className="al-table" data-testid="stat-recent">
                <thead><tr><th>Zeitpunkt</th><th>Ereignis</th><th>Details</th></tr></thead>
                <tbody>
                  {stats && stats.recent.map((r, i) => (
                    <tr key={i}>
                      <td style={{ whiteSpace: "nowrap", color: "#6c757d" }}>{fmtTs(r.ts)}</td>
                      <td style={{ fontWeight: 600 }}>{label(r.event)}</td>
                      <td style={{ color: "#6c757d", fontSize: "0.85rem" }}>
                        {r.meta && Object.keys(r.meta).length ? Object.entries(r.meta).map(([k, v]) => `${k}: ${v}`).join(", ") : "—"}
                      </td>
                    </tr>
                  ))}
                  {stats && stats.recent.length === 0 && <tr><td colSpan={3} style={{ color: "#6c757d" }}>Noch keine Aktivitäten.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
